import React, { useState, useEffect, useCallback, useRef } from 'react';
import { api } from '../utils/api';
import { Users, Filter, TrendingUp, AlertTriangle, BookOpen, ChevronRight, Target, Activity } from 'lucide-react';
import { acqDenominator, invalidSeatCount } from '../utils/seat-rate.mjs';
// 分析画面は Lucian UX/UI Prompt の部品に寄せる（ADR 0034）。各部品の出典とライセンスは部品のファイルの先頭。
import '../components/lucian/lucian.css';
import PeriodSegmented from '../components/lucian/PeriodSegmented';
import MetricHero from '../components/lucian/MetricHero';
import ConversionFunnel from '../components/lucian/ConversionFunnel';
import BulletCompare from '../components/lucian/BulletCompare';
import RankBars from '../components/lucian/RankBars';
import DonutBreakdown from '../components/lucian/DonutBreakdown';
import Skeleton from '../components/lucian/Skeleton';
import RetryPanel from '../components/lucian/RetryPanel';

/* ====== デザイントークン ====== */
// 色の実値は index.css と components/lucian/lucian.css に置く。ここは文字色の別名だけ。
const colors = {
  text: 'var(--ink)',
  textDim: 'var(--ink-soft)',
  textMute: 'var(--ink-mute)',
  primaryLight: 'var(--mark)',
  warning: 'var(--hold)',
};

const inputStyle = {
  width: '100%',
  padding: '11px 12px',
  background: 'var(--field)',
  border: '1px solid var(--rule-strong)',
  borderRadius: 'var(--r-sm)',
  color: colors.text,
  fontSize: 'var(--t-body)',
  outline: 'none',
  colorScheme: 'dark',
  minHeight: 44,
};

/* ====== サブコンポーネント ====== */
// 数字の札。色は左の小さな点だけが持ち、数字は文字の色（dataviz：文字にデータの色を塗らない）。
function Stat({ label, value, dot, sub }) {
  return (
    <div className="lx-stat">
      <small>{dot && <span className="lx-dot" style={{ background: dot }} />}{label}</small>
      <b>{value}</b>
      {sub && <em>{sub}</em>}
    </div>
  );
}

const PRESETS = [
  { value: 'today', label: '今日' },
  { value: 'week', label: '直近7日' },
  { value: 'month', label: '今月' },
  { value: 'custom', label: 'カスタム' },
];

/* ====== 日付ヘルパー ====== */
const toYmd = (d) => d.toLocaleDateString('sv-SE');
const today = () => toYmd(new Date());
const startOfMonth = () => {
  const d = new Date();
  return toYmd(new Date(d.getFullYear(), d.getMonth(), 1));
};
const endOfMonth = () => {
  const d = new Date();
  return toYmd(new Date(d.getFullYear(), d.getMonth() + 1, 0));
};

/* ====== 弱点診断ヘルパー ====== */
const RATE_STAGES_HOME = [
  { key: 'catchToSticker',  label: 'C → ティッシュ率',          num: 'sticker',  den: 'catch'    },
  { key: 'stickerToTalk',   label: 'ティッシュ → トーク開始率', num: 'talk',     den: 'sticker'  },
  { key: 'talkToFulltalk',  label: 'トーク開始 → フル率',   num: 'fulltalk', den: 'talk'     },
  { key: 'fulltalkToSeat',  label: 'フルトーク → 着座率',   num: 'seat',     den: 'fulltalk' },
  { key: 'seatToAcq',       label: '着座 → 獲得率',         num: 'acq',      den: 'valid_seat' }, // 分母は有効着座（ADR 0028）
];

function homeCalcRates(d) {
  if (!d) return null;
  return {
    catch: d.catch || 0, sticker: d.sticker || 0, talk: d.talk || 0, fulltalk: d.fulltalk || 0, seat: d.seat || 0, acq: d.acq || 0,
    valid_seat: acqDenominator(d),
    catchToSticker: (d.catch || 0) > 0 ? (d.sticker || 0) / d.catch : 0,
    stickerToTalk:  (d.sticker || 0) > 0 ? (d.talk || 0) / d.sticker : 0,
    talkToFulltalk: (d.talk || 0) > 0 ? (d.fulltalk || 0) / d.talk : 0,
    fulltalkToSeat: (d.fulltalk || 0) > 0 ? (d.seat || 0) / d.fulltalk : 0,
    seatToAcq:      acqDenominator(d) > 0 ? (d.acq || 0) / acqDenominator(d) : 0, // 分母は有効着座（ADR 0028）
  };
}

/* ====== メインコンポーネント ====== */
export default function Home({ user, onNavigate }) {
  const [dashboard, setDashboard] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  // 読み込みに失敗したとき（#090）。前回の数字は消さずに残し、自動の再試行は最大3回（home-viz.mjs）。
  const [loadError, setLoadError] = useState(false);
  const [attempt, setAttempt] = useState(0);
  // 大きな数字に出す指標（#098）。キャッチャーは獲得を取らないので着座を既定にする。
  const [heroKey, setHeroKey] = useState((user.job_type || 'both') === 'catcher' ? 'seat' : 'acq');
  const [teamData, setTeamData] = useState(null); // 弱点診断用
  const [myFeedback, setMyFeedback] = useState([]); // 自分に届いたフィードバック（最新順）
  // キャッチャーは着座までが仕事で契約を取らないため、獲得率は常に0%になる。
  // 弱点診断やチーム平均を歪めるので、獲得に関する率は出さない（ADR 0014）。
  const isCatcher = (user.job_type || 'both') === 'catcher';
  // クローザーの着座は「渡されてクローズした件数」も含む（ADR 0017）ので、
  // 獲得率の母数は役割に関係なく着座でよい。役割による出し分けはキャッチャーだけ。

  // フィルターは既定で畳む。ダッシュボードの主役は数字であって操作パネルではない。
  const [showFilter, setShowFilter] = useState(false);
  // フィルター
  const [preset, setPreset] = useState('today'); // today | week | month | custom
  const [dateFrom, setDateFrom] = useState(today());
  const [dateTo, setDateTo] = useState(today());
  // 初期表示は「自分」。担当者ボタンは複数選択（トグル）で、**空配列＝全員**。
  // 「全員」を明示的な値ではなく空で表すのは、APIの user_ids を省略＝絞り込みなし、と揃えるため。
  const [selectedUserIds, setSelectedUserIds] = useState([String(user.id)]);
  const toggleUser = (id) => setSelectedUserIds(prev =>
    prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]
  );

  const applyPreset = (p) => {
    setPreset(p);
    const now = new Date();
    if (p === 'today') {
      setDateFrom(today()); setDateTo(today());
    } else if (p === 'week') {
      setDateFrom(toYmd(new Date(now - 6 * 86400000))); setDateTo(today());
    } else if (p === 'month') {
      setDateFrom(startOfMonth()); setDateTo(endOfMonth());
    }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { date_from: dateFrom, date_to: dateTo };
      if (selectedUserIds.length) params.user_ids = selectedUserIds.join(',');
      const d = await api.getAnalyticsDashboard(params);
      setDashboard(d);
      setLoadError(false);
      setAttempt(0);
    } catch {
      // 前回の数字は残す（空で上書きすると「0件」に見えて誤解を招く）
      setLoadError(true);
    } finally {
      setLoading(false);
    }
  }, [dateFrom, dateTo, selectedUserIds]);
  const retry = () => { setAttempt(a => a + 1); load(); };

  // 取得に失敗しても直前の一覧を消さない。空で上書きすると担当者ボタンが「全員/自分」だけになり、
  // 対象欄も名前を引けなくなる（開発時は StrictMode の2重マウントで実際に起きた）。
  useEffect(() => {
    api.getUsers()
      .then(d => { if (Array.isArray(d) && d.length) setUsers(d); })
      .catch(() => {});
  }, []);

  // 自分に届いたフィードバック（最新5件）。ホーム最上部に常設表示して見落としを防ぐ。
  useEffect(() => {
    api.getMyFeedback(5).then(d => setMyFeedback(Array.isArray(d) ? d : [])).catch(() => setMyFeedback([]));
  }, []);

  useEffect(() => { load(); }, [load]);

  // 日付またぎ対策：PWAを開いたまま日付が変わったら、相対プリセット（今日/直近7日/今月）を
  // 新しい今日で再計算する（「今日」のまま前日の数字を表示し続けるのを防ぐ）。
  const dayRef = useRef(today());
  useEffect(() => {
    const check = () => {
      const t = today();
      if (t === dayRef.current) return;
      dayRef.current = t;
      if (preset !== 'custom') applyPreset(preset);
    };
    const onVis = () => { if (document.visibilityState === 'visible') check(); };
    const id = setInterval(check, 30000);
    document.addEventListener('visibilitychange', onVis);
    window.addEventListener('focus', check);
    return () => { clearInterval(id); document.removeEventListener('visibilitychange', onVis); window.removeEventListener('focus', check); };
  }, [preset]);

  // 弱点診断用：チーム全体データ（フィルタ無視）を取得
  useEffect(() => {
    // 今月の範囲で固定取得（弱点診断は今月ベース）
    const now = new Date();
    const from = toYmd(new Date(now.getFullYear(), now.getMonth(), 1));
    const to = toYmd(new Date(now.getFullYear(), now.getMonth() + 1, 0));
    api.getAnalyticsDashboard({ date_from: from, date_to: to })
      .then(setTeamData).catch(() => setTeamData(null));
  }, []);

  // 弱点診断（自分 vs チーム）
  const weaknessSummary = (() => {
    if (!teamData) return null;
    const byUserAll = teamData.byUser || [];
    const selfRow = byUserAll.find(u => u.user_id === user.id);
    if (!selfRow || (selfRow.catch || 0) === 0) return null;
    const selfRates = homeCalcRates(selfRow);
    // チーム平均（重み付き）
    const tot = byUserAll.filter(u => (u.catch || 0) > 0 || (u.seat || 0) > 0).reduce((a, u) => ({
      catch: a.catch + (u.catch || 0), sticker: a.sticker + (u.sticker || 0),
      talk: a.talk + (u.talk || 0), fulltalk: a.fulltalk + (u.fulltalk || 0), seat: a.seat + (u.seat || 0), acq: a.acq + (u.acq || 0),
      valid_seat: a.valid_seat + acqDenominator(u),
    }), { catch: 0, sticker: 0, talk: 0, fulltalk: 0, seat: 0, acq: 0, valid_seat: 0 });
    const teamRates = homeCalcRates(tot);
    if (!teamRates) return null;
    // 最大ギャップ
    const stages = isCatcher ? RATE_STAGES_HOME.filter(s => s.key !== 'seatToAcq') : RATE_STAGES_HOME;
    const gaps = stages.map(s => ({
      key: s.key, label: s.label,
      self: selfRates[s.key], team: teamRates[s.key],
      gap: teamRates[s.key] - selfRates[s.key],
      den: selfRates[s.den],
    })).filter(g => g.den >= 5 && g.gap > 0);
    if (gaps.length === 0) return { allAbove: true };
    const top = gaps.sort((a, b) => b.gap - a.gap)[0];

    // この段階のチームリーダー（自分以外）
    const eligible = byUserAll.filter(u => u.user_id !== user.id && (u[stages.find(s => s.key === top.key).den] || 0) >= 30);
    const stage = stages.find(s => s.key === top.key);
    const leader = eligible.length ? eligible.reduce((b, u) => {
      const r = (u[stage.den] || 0) > 0 ? (u[stage.num] || 0) / u[stage.den] : 0;
      const br = b ? ((b[stage.den] || 0) > 0 ? (b[stage.num] || 0) / b[stage.den] : 0) : -1;
      return r > br ? u : b;
    }, null) : null;
    const leaderRate = leader ? (leader[stage.num] || 0) / leader[stage.den] : 0;
    return { weakness: top, leader, leaderRate };
  })();

  const m = dashboard?.metrics || {};
  // 着座は「関わった件数」なので、クローザーでも母数は着座でよい（ADR 0017）。
  const lostRanking = dashboard?.lostRanking || [];
  const byUser = dashboard?.byUser || [];

  // 一覧が未取得でも、自分の名前は user から引けるので ID 表示に落とさない。
  const nameOfUser = (id) => {
    const found = users.find(u => String(u.id) === String(id));
    if (found) return found.name;
    return String(id) === String(user.id) ? user.name : `#${id}`;
  };
  const selectedUserName = selectedUserIds.length === 0
    ? '全員'
    : selectedUserIds.map(nameOfUser).join('・');

  // 集計期間ラベル（何月分か）
  const periodLabel = (() => {
    const from = new Date(dateFrom);
    const to = new Date(dateTo);
    const fromYm = `${from.getFullYear()}年${from.getMonth() + 1}月`;
    const toYm = `${to.getFullYear()}年${to.getMonth() + 1}月`;
    const fromY = from.getFullYear(), fromM = from.getMonth();
    const toY = to.getFullYear(), toM = to.getMonth();

    // 同月内
    if (fromY === toY && fromM === toM) {
      // 月の1日〜末日 → "X月分"
      const firstOfMonth = new Date(fromY, fromM, 1).toLocaleDateString('sv-SE');
      const lastOfMonth = new Date(fromY, fromM + 1, 0).toLocaleDateString('sv-SE');
      if (dateFrom === firstOfMonth && dateTo === lastOfMonth) {
        return `${fromYm}分`;
      }
      // 同日
      if (dateFrom === dateTo) {
        const today_ = new Date().toLocaleDateString('sv-SE');
        if (dateFrom === today_) return `今日 (${dateFrom})`;
        return `${dateFrom}`;
      }
      return `${fromYm} (${from.getDate()}日〜${to.getDate()}日)`;
    }
    // 跨月
    return `${fromYm} 〜 ${toYm}`;
  })();

  const daily = dashboard?.dailyFunnel || [];
  const heroItems = [
    { key: 'catch',    label: 'キャッチ',     value: m.catch    || 0 },
    { key: 'sticker',  label: 'ティッシュ',   value: m.sticker  || 0 },
    { key: 'talk',     label: 'トーク開始',   value: m.talk     || 0 },
    { key: 'fulltalk', label: 'フルトーク',   value: m.fulltalk || 0 },
    { key: 'seat',     label: '着座',         value: m.seat     || 0 },
    { key: 'acq',      label: '獲得',         value: m.acq      || 0 },
  ];
  const hasAny = heroItems.some(i => i.value > 0);
  const firstLoad = loading && !dashboard;
  const staffRows = byUser.filter(u => (u.seat || 0) > 0 || (u.catch || 0) > 0);
  const lostTotal = lostRanking.reduce((s, x) => s + x.cnt, 0);

  return (
    <div className="lx" style={{ maxWidth: 1120, margin: '0 auto', padding: 'var(--s6) var(--s4) var(--s8)' }}>
      {/* ヘッダー */}
      <div style={{ marginBottom: 18 }}>
        <div style={{ fontSize: 'var(--t-note)', color: colors.textMute, marginBottom: 'var(--s1)' }}>{user.name} さん</div>
        <h1 className="page-title" style={{ marginBottom: 0 }}>KPI ダッシュボード</h1>
      </div>

      {/* あなたへのフィードバック（最上部に常設・見落とし防止） */}
      {myFeedback.length > 0 && (
        <button type="button" className="lx-card" onClick={() => onNavigate && onNavigate('reflections-history')}
          style={{ display: 'block', width: '100%', textAlign: 'left', border: 0, boxShadow: '0 0 0 1px var(--mark-soft), inset 3px 0 0 var(--mark)' }}>
          <div className="lx-card__head" style={{ color: colors.primaryLight }}>
            あなたへのフィードバック
            <span className="lx-card__aside">日報履歴で見る →</span>
          </div>
          {myFeedback.slice(0, 3).map((f, i, arr) => {
            const dt = new Date(f.reflection_date + 'T00:00:00Z');
            const label = `${dt.getUTCMonth() + 1}/${dt.getUTCDate()}`;
            const last = i === arr.length - 1;
            return (
              <div key={f.id} style={{ marginBottom: last ? 0 : 10, paddingBottom: last ? 0 : 10, borderBottom: last ? 'none' : '1px solid var(--rule)' }}>
                <div style={{ fontSize: 'var(--t-note)', color: colors.textMute, marginBottom: 3 }}>
                  {label}{f.feedback_by_name ? ` ・ ${f.feedback_by_name}より` : ''}
                </div>
                <div style={{ fontSize: i === 0 ? 15 : 14, color: colors.text, whiteSpace: 'pre-wrap', lineHeight: 1.55, fontWeight: i === 0 ? 600 : 400 }}>
                  {f.feedback}
                </div>
              </div>
            );
          })}
        </button>
      )}

      {/* 今月の弱点（#163 ブレットチャート：自分の棒とチーム平均の目印を1本に） */}
      {weaknessSummary && !weaknessSummary.allAbove && (
        <div className="lx-card">
          <div className="lx-card__head"><Target size={15} /> あなたの今月の弱点</div>
          <BulletCompare
            label={weaknessSummary.weakness.label}
            self={weaknessSummary.weakness.self}
            team={weaknessSummary.weakness.team}
            leader={weaknessSummary.leader ? weaknessSummary.leaderRate : null}
            leaderName={weaknessSummary.leader ? `${weaknessSummary.leader.user_name}さん` : null}
          />
          {weaknessSummary.leader && (
            <div style={{ marginTop: 10, fontSize: 'var(--t-note)', color: 'var(--gain)' }}>
              <strong>{weaknessSummary.leader.user_name}さん</strong>に学ぼう（{(weaknessSummary.leaderRate * 100).toFixed(1)}%）
            </div>
          )}
        </div>
      )}
      {weaknessSummary?.allAbove && (
        <div className="lx-card" style={{ fontSize: 'var(--t-note)', color: 'var(--gain)', fontWeight: 700, textAlign: 'center' }}>
          今月、全段階でチーム平均以上！この調子で量を増やしましょう
        </div>
      )}

      {/* 期間（#047）。いちばん触る条件なので畳まずに出す。担当者と日付は下で開いて変える */}
      <div style={{ marginBottom: 'var(--s3)' }}>
        <PeriodSegmented options={PRESETS} value={preset} onChange={applyPreset} />
      </div>

      {/* 条件（既定は畳む。いまの条件は見出しに出す） */}
      <div className="lx-card">
        <button type="button" onClick={() => setShowFilter(v => !v)} className="filterbar">
          <Filter size={16} color="var(--ink-soft)" style={{ flexShrink: 0 }} />
          <span className="filterbar__period">{periodLabel}</span>
          <span className="filterbar__who">{selectedUserName}</span>
          <span className="filterbar__action">{showFilter ? '閉じる' : '条件を変える'}</span>
        </button>
        <div style={{ display: showFilter ? 'block' : 'none', marginTop: 'var(--s3)' }}>
          {/* 期間 */}
          <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 8, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 'var(--t-note)', fontWeight: 700, color: colors.textMute, marginBottom: 4, display: 'block' }}>開始日</label>
              <input type="date" value={dateFrom} onChange={e => { setDateFrom(e.target.value); setPreset('custom'); }} style={inputStyle} />
            </div>
            <div>
              <label style={{ fontSize: 'var(--t-note)', fontWeight: 700, color: colors.textMute, marginBottom: 4, display: 'block' }}>終了日</label>
              <input type="date" value={dateTo} onChange={e => { setDateTo(e.target.value); setPreset('custom'); }} style={inputStyle} />
            </div>
          </div>

          {/* 担当者（複数選択・初期は自分／「全員」で解除） */}
          <div>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 6, gap: 8 }}>
              <label style={{ fontSize: 'var(--t-note)', fontWeight: 700, color: colors.textMute }}>
                <Users size={11} style={{ verticalAlign: 'middle', marginRight: 4 }} />担当者で絞り込み（複数選択可）
              </label>
              {selectedUserIds.length > 1 && (
                <span style={{ fontSize: 'var(--t-note)', fontWeight: 700, color: colors.primaryLight }}>
                  {selectedUserIds.length}人の合計
                </span>
              )}
            </div>
            <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
              {(() => {
                const meId = String(user.id);
                const others = users.filter(u => u.is_active !== 0 && String(u.id) !== meId);
                const all = selectedUserIds.length === 0;
                const chip = (active) => ({
                  padding: '9px 14px', borderRadius: 12, minHeight: 44,
                  border: 0, boxShadow: `0 0 0 ${active ? 1.5 : 1}px ${active ? 'var(--mark)' : 'var(--rule)'}`,
                  background: active ? 'var(--mark-soft)' : 'var(--paper-sunk)',
                  color: active ? colors.primaryLight : colors.textDim,
                  fontWeight: 700, fontSize: 'var(--t-note)', cursor: 'pointer',
                  display: 'flex', alignItems: 'center', gap: 6,
                  transition: 'background-color .2s, box-shadow .2s, transform .3s var(--ux-spring)',
                });
                return (
                  <>
                    {/* 「全員」は絞り込みの解除。トグルではないので単独で扱う。 */}
                    <button key="all" type="button" aria-pressed={all} onClick={() => setSelectedUserIds([])} style={chip(all)}>全員</button>
                    {[{ id: meId, label: '自分' }, ...others.map(u => ({ id: String(u.id), label: u.name }))].map(b => {
                      const active = selectedUserIds.includes(b.id);
                      return (
                        <button key={b.id} type="button" aria-pressed={active} onClick={() => toggleUser(b.id)} style={chip(active)}>
                          <span aria-hidden="true" style={{
                            width: 16, height: 16, borderRadius: 5, flexShrink: 0,
                            boxShadow: `inset 0 0 0 1.5px ${active ? 'var(--mark)' : 'var(--rule-strong)'}`,
                            background: active ? 'var(--mark)' : 'transparent',
                            color: 'var(--paper)', fontSize: 12, lineHeight: '16px', fontWeight: 900, textAlign: 'center',
                          }}>{active ? '✓' : ''}</span>
                          {b.label}
                        </button>
                      );
                    })}
                  </>
                );
              })()}
            </div>
          </div>
        </div>
      </div>

      {loadError && <RetryPanel attempt={attempt} onRetry={retry} hasStale={!!dashboard} />}

      {firstLoad ? (
        <Skeleton blocks={[[150, 60], [260, 100], [160, 100]]} />
      ) : dashboard && (
        // 読み込み直しの間は、前の数字を薄くして残す（骨組みに戻すと画面が跳ねる。dataviz）
        <div className={loading ? 'lx-refetching' : ''} aria-busy={loading}>

          {/* 稼働終了入力未入力の案内 */}
          {m.seat > 0 && (m.catch === 0 && m.sticker === 0 && m.talk === 0 && m.fulltalk === 0) && (
            <button
              type="button"
              onClick={() => onNavigate && onNavigate('shift-end')}
              className="lx-card lx-reveal"
              style={{
                width: '100%', border: 0, boxShadow: '0 0 0 1px #5C4718',
                background: 'var(--hold-bg)', color: 'var(--hold)',
                fontSize: 'var(--t-note)', fontWeight: 600, textAlign: 'left',
                display: 'flex', alignItems: 'center', gap: 8,
              }}
            >
              <BookOpen size={18} style={{ flexShrink: 0 }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 700, marginBottom: 2 }}>キャッチ・ティッシュ・トークが未入力</div>
                <div style={{ fontSize: 'var(--t-note)', fontWeight: 500, opacity: 0.85 }}>「日報（稼働終了）」でカウンター合計を入れると正確なファネルが見えます</div>
              </div>
              <ChevronRight size={18} style={{ flexShrink: 0 }} />
            </button>
          )}

          {!hasAny ? (
            /* データが無いとき（#092）：条件をゆるめるボタンを出す */
            <div className="lx-card lx-empty lx-reveal">
              <div className="lx-empty__title">この条件ではまだ記録がありません</div>
              <div className="lx-empty__body">{periodLabel}・{selectedUserName}</div>
              <div className="lx-empty__actions">
                {preset === 'today' && <button type="button" className="lx-btn" onClick={() => applyPreset('week')}>直近7日を見る</button>}
                {preset !== 'month' && <button type="button" className="lx-btn" onClick={() => applyPreset('month')}>今月を見る</button>}
                {selectedUserIds.length > 0 && <button type="button" className="lx-btn" onClick={() => setSelectedUserIds([])}>全員を見る</button>}
              </div>
            </div>
          ) : (
            <>
              {/* 主要な数字（#098）：変わった桁だけ回る。下の線は日ごとの推移 */}
              <div className="lx-card lx-reveal" style={{ '--d': '0ms' }}>
                <MetricHero items={heroItems} selected={heroKey} onSelect={setHeroKey} daily={daily} periodLabel={periodLabel} />
              </div>

              {/* ファネル（#160）：段を押すと率が出る。下の表は同じ数字 */}
              <div className="lx-card lx-reveal" style={{ '--d': '60ms' }}>
                <div className="lx-card__head"><TrendingUp size={15} /> ファネル</div>
                <ConversionFunnel metrics={m} hideAcq={isCatcher} topLostReason={lostRanking[0] || null} />
                {!isCatcher && invalidSeatCount(m) > 0 && (
                  <div style={{ marginTop: 8, fontSize: 'var(--t-note)', color: colors.textMute }}>
                    獲得の率の分母は有効着座 {acqDenominator(m)}（「有効じゃない」{invalidSeatCount(m)}件を除く）
                  </div>
                )}
              </div>

              {/* 着座メモの結果・キャッチとクローズの内訳 */}
              {m.seat > 0 && (
                <div className="lx-card lx-reveal" style={{ '--d': '120ms' }}>
                  <div className="lx-card__head"><Activity size={15} /> 着座メモの結果</div>
                  <div className="lx-stats">
                    <Stat label="獲得" value={m.acq || 0} dot="var(--gain)" />
                    <Stat label="失注" value={m.lost || 0} dot="var(--loss)" />
                    <Stat label="後追い" value={m.followup || 0} dot="var(--mark)" />
                  </div>
                  {((m.passed || 0) + (m.received || 0)) > 0 && (
                    <>
                      <div className="lx-card__head" style={{ marginTop: 16 }}>キャッチとクローズの内訳</div>
                      <div className="lx-stats">
                        <Stat label="自分で完結" value={m.selfSeat || 0} sub={`獲得 ${m.selfAcq || 0}`} />
                        <Stat label="渡した" value={m.passed || 0} sub="獲得は相手に計上" />
                        <Stat label="受け取った" value={m.received || 0} sub={`獲得 ${m.receivedAcq || 0}`} />
                      </div>
                      <div style={{ fontSize: 'var(--t-note)', color: colors.textMute, marginTop: 8, lineHeight: 1.7 }}>
                        {selectedUserIds.length === 1 ? (
                          <>着座 {m.seat || 0} ＝ 自分で完結 {m.selfSeat || 0} ＋ 渡した {m.passed || 0} ＋ 受け取った {m.received || 0}</>
                        ) : (
                          <>
                            着座 {m.seat || 0}（実件数）＝ 自分で完結 {m.selfSeat || 0} ＋ 受け渡し {m.passed || 0}<br />
                            ※ 受け渡しの1件は、渡した人と受け取った人の両方の着座に数えます。ここの合計は
                            メモの実件数なので、担当者ごとの着座を足した数より少なくなります。
                          </>
                        )}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* 担当者別（#246）。上が複数人の合計になっているときだけ出す
                  （全員＝0人選択、または2人以上選択）。1人だけの時は画面全体がその人なので不要。 */}
              {selectedUserIds.length !== 1 && staffRows.length > 0 && (
                <div className="lx-card lx-reveal" style={{ '--d': '180ms' }}>
                  <div className="lx-card__head"><Users size={15} /> 担当者別<span className="lx-card__aside">獲得の多い順・押すとその人だけ</span></div>
                  <RankBars
                    label="担当者別の獲得"
                    rows={staffRows}
                    onPick={(r) => setSelectedUserIds([String(r.user_id)])}
                    renderNums={(r) => {
                      const catcher = (users.find(u => u.id === r.user_id)?.job_type || 'both') === 'catcher';
                      const den = acqDenominator(r);
                      return (
                        <>
                          着座 {r.seat || 0}<b>{r.acq || 0}</b>獲得
                          {!catcher && den > 0 && <> ・ {((r.acq / den) * 100).toFixed(0)}%</>}
                        </>
                      );
                    }}
                    renderSub={(r) => (
                      <>
                        <span>キャッチ {r.catch || 0}</span>
                        <span>ティッシュ {r.sticker || 0}</span>
                        <span>トーク {r.talk || 0}</span>
                        <span>フル {r.fulltalk || 0}</span>
                        {/* 渡し合いがあった人だけ、キャッチとクローズの内訳（ADR 0016） */}
                        {(r.passed || 0) > 0 && <span>渡した {r.passed}</span>}
                        {(r.received || 0) > 0 && <span>受け取った {r.received}</span>}
                        {invalidSeatCount(r) > 0 && <span>有効着座 {acqDenominator(r)}</span>}
                      </>
                    )}
                  />
                </div>
              )}

              {/* 失注理由（#097）：上位3つと「その他」。下の表で全部の理由と件数が読める */}
              {lostRanking.length > 0 && (
                <div className="lx-card lx-reveal" style={{ '--d': '240ms' }}>
                  <div className="lx-card__head"><AlertTriangle size={15} color={colors.warning} /> 失注理由<span className="lx-card__aside">押すとその理由を外して割り直す</span></div>
                  {/* 条件を変えたら、外した理由を元に戻す（表の件数と円の合計がずれないように） */}
                  <DonutBreakdown key={`${dateFrom}|${dateTo}|${selectedUserIds.join(',')}`} label="失注理由の割合" rows={lostRanking.map(r => ({ label: r.lost_reason, n: r.cnt }))} />
                  <table className="lx-table" aria-label="失注理由の一覧">
                    <thead><tr><th>順位・理由</th><th>件数</th><th>割合</th></tr></thead>
                    <tbody>
                      {lostRanking.map((r, i) => (
                        <tr key={r.lost_reason}>
                          <td>{i + 1}. {r.lost_reason}</td>
                          <td>{r.cnt}</td>
                          <td>{lostTotal > 0 ? `${Math.round((r.cnt / lostTotal) * 100)}%` : '-'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}

              {/* 注意指標 */}
              {(m.meterMissing > 0 || m.familyLost > 0) && (
                <div className="lx-card lx-reveal" style={{ '--d': '300ms' }}>
                  <div className="lx-card__head">注意指標</div>
                  <div className="lx-stats" style={{ gridTemplateColumns: '1fr 1fr' }}>
                    <Stat label="検針票なし/ログイン不可" value={m.meterMissing || 0} dot="var(--hold)" sub="着座中" />
                    <Stat label="名義人・家族確認失注" value={m.familyLost || 0} dot="var(--loss)" sub="着座中" />
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      )}
    </div>
  );
}
